/**
 * ExpedienteDetail's audit-backed activity surface — ONE collapsed-by-default
 * section ("Actividad y versiones") that expands into a SINGLE unified,
 * chronological timeline (day-grouped, newest first) over
 * `useExpedienteActivity`. Every entry is already decorated server-side with
 * `versionSeq`/`restorable` (the entry's paired `expediente_versions`
 * projection row, when one exists) — there is no second "Versiones" lane
 * anymore; restore lives directly on the same row/drawer.
 *
 * Clicking a row opens ONE shared wide drawer:
 *  - Non-restorable entries show only the before→after diff ("Cambios").
 *  - Restorable entries additionally show an "Expediente completo" tab — the
 *    FULL read-only case-aggregate snapshot at that version (fetched lazily
 *    via `useExpedienteVersionDetail` only once that tab opens), plus a
 *    "Restaurar esta versión" affordance available from either tab.
 */
import { useEffect, useState } from 'react';
import type { ExpedienteActivityEntry, ExpedienteVersionDetailResponse } from '@cumpli/shared';
import { esPA } from '@cumpli/shared';
import {
  formatDate,
  formatRelative,
  useExpedienteActivity,
  useExpedienteVersionDetail,
  useRestoreExpedienteVersion,
} from '../lib/hooks';
import { activityCategoryLabel, auditActionLabel } from '../lib/audit-labels';
import { diffActivityFields, FIELD_LABELS, formatDiffValue } from '../lib/activity-diff';
import { Badge, Button, Card, EmptyState, Pill, Skeleton, SlideOver, type Tone } from './ui';
import { TabStrip } from './compliance';
import {
  AlertTriangle,
  CalendarClock,
  ChevronDown,
  FileCheck,
  FileText,
  History,
  RotateCcw,
  Search,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';

const CATEGORY_ICON: Record<string, LucideIcon> = {
  expediente: FileText,
  expediente_document: FileText,
  scheduled_review: CalendarClock,
  credit_consent: FileCheck,
  subject_profile: UserRound,
  subject_owner: Users,
  alert: AlertTriangle,
  screening_batch: Search,
  screening_rescreen: Search,
  watchlist_refresh: Search,
};

/** Always returns a concrete icon (fallback for an entityType this build doesn't recognize). */
function categoryIcon(entityType: string): LucideIcon {
  return CATEGORY_ICON[entityType] ?? History;
}

// How many rows the timeline preview shows before "Ver más".
const PREVIEW_LIMIT = 6;

/** "18 de julio de 2026" — the day-group header for the preview timeline. */
function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('es-PA', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}

interface DayGroup {
  day: string;
  rows: ExpedienteActivityEntry[];
}

/** Groups already-sorted (newest-first) entries into consecutive same-day buckets. */
function groupByDay(entries: ExpedienteActivityEntry[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const entry of entries) {
    const day = dayLabel(entry.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.rows.push(entry);
    else groups.push({ day, rows: [entry] });
  }
  return groups;
}

const ESTADO_TONE: Record<string, Tone> = {
  Nuevo: 'slate',
  'En Revisión': 'blue',
  'Docs Pendientes': 'amber',
  Escalado: 'red',
  Aprobado: 'green',
  Activo: 'green',
  Rechazado: 'red',
  'Actualización Requerida': 'amber',
};
const RIESGO_TONE: Record<string, Tone> = { bajo: 'green', medio: 'amber', alto: 'red', sin: 'slate' };
const DOC_ESTADO_TONE: Record<string, Tone> = {
  Vigente: 'green',
  Aceptado: 'green',
  'Por Vencer': 'amber',
  Solicitado: 'amber',
  Vencido: 'red',
  'Bajo Revisión': 'blue',
  Recibido: 'blue',
};

function ActivityRow({
  entry,
  onSelect,
}: {
  entry: ExpedienteActivityEntry;
  onSelect: () => void;
}) {
  const Icon = categoryIcon(entry.entityType);
  const when = formatRelative(entry.createdAt) || formatDate(entry.createdAt, '');
  return (
    <button
      onClick={onSelect}
      aria-label={`${esPA.expedienteActivity.viewDetail} — ${auditActionLabel(entry.action)}`}
      className="flex w-full items-center gap-3 rounded-xl border border-line bg-canvas px-4 py-3 text-left transition-colors hover:border-ink-faint"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-panel text-ink-soft">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-ink">
          {auditActionLabel(entry.action)}
        </span>
        {entry.targetLabel && (
          <span className="block truncate text-sm text-ink-soft">{entry.targetLabel}</span>
        )}
        <span className="mt-0.5 block truncate text-[11.5px] text-ink-faint">
          {activityCategoryLabel(entry.entityType)} · {entry.actorName} · {when}
        </span>
      </span>
      {entry.restorable && entry.versionSeq !== null && (
        <span className="flex shrink-0 items-center gap-1.5">
          <Pill tone="slate">
            {esPA.expedienteActivity.versionLabel} {entry.versionSeq}
          </Pill>
          <Pill tone="green">{esPA.expedienteActivity.restorableBadge}</Pill>
        </span>
      )}
    </button>
  );
}

function LoadingRows() {
  return (
    <div className="grid gap-2.5">
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  );
}

function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-tone-red">{message}</p>
      <Button variant="outline" onClick={onRetry}>
        Reintentar
      </Button>
    </div>
  );
}

/** "Ver más (N)" / "Ver menos" toggle for the client-side preview slice. */
function ExpandToggle({
  expanded,
  hiddenCount,
  onToggle,
}: {
  expanded: boolean;
  hiddenCount: number;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="mt-3 flex w-full items-center justify-center gap-1 rounded-xl border border-dashed border-line-strong py-2.5 text-xs font-semibold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark"
    >
      {expanded ? 'Ver menos' : `Ver más (${hiddenCount})`}
      <ChevronDown
        className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
        strokeWidth={2}
      />
    </button>
  );
}

/** The unified timeline: a compact preview (newest first, grouped by day),
 *  expandable via "Ver más", plus a cursor "Ver más" once expanded that
 *  fetches the NEXT server page (distinct from the client-side preview
 *  slice). Clicking a row opens the shared drawer. */
function ActivityTimeline({
  entries,
  isLoading,
  isError,
  onRetry,
  onSelect,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  entries: ExpedienteActivityEntry[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (entry: ExpedienteActivityEntry) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, PREVIEW_LIMIT);
  const groups = groupByDay(visible);

  if (isLoading) return <LoadingRows />;
  if (isError) return <ErrorRow message="No se pudo cargar la actividad." onRetry={onRetry} />;
  if (entries.length === 0) {
    return (
      <EmptyState icon={<History className="h-8 w-8" strokeWidth={1.5} />} title={esPA.expedienteActivity.empty} />
    );
  }

  return (
    <>
      <div className="grid gap-4 [&>*]:min-w-0">
        {groups.map((g) => (
          <div key={g.day}>
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-faint">{g.day}</div>
            <div className="grid gap-2 [&>*]:min-w-0">
              {g.rows.map((entry) => (
                <ActivityRow key={entry.versionKey} entry={entry} onSelect={() => onSelect(entry)} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {entries.length > PREVIEW_LIMIT && (
        <ExpandToggle
          expanded={expanded}
          hiddenCount={entries.length - PREVIEW_LIMIT}
          onToggle={() => setExpanded((v) => !v)}
        />
      )}
      {/* Beyond the client-side preview slice: fetches the NEXT server page —
       *  only surfaced once the preview is fully expanded, so it never
       *  competes with ExpandToggle above. */}
      {expanded && hasNextPage && (
        <button
          onClick={onLoadMore}
          disabled={isFetchingNextPage}
          className="mt-3 flex w-full items-center justify-center gap-1 rounded-xl border border-dashed border-line-strong py-2.5 text-xs font-semibold text-ink-soft transition-colors hover:border-primary hover:text-primary-dark disabled:opacity-60"
        >
          {isFetchingNextPage ? esPA.expedienteActivity.loadingMoreVersions : esPA.expedienteActivity.loadMoreVersions}
        </button>
      )}
    </>
  );
}

/** Narrows a version-detail response's `afterState` down to its `.expediente`
 *  sub-object (case-aggregate snapshot shape: `{ schemaVersion, expediente,
 *  documents }`), safely, out of the loosely-typed aggregate. */
function snapshotExpediente(
  detail: ExpedienteVersionDetailResponse | undefined
): Record<string, unknown> | null {
  const exp = detail?.afterState?.['expediente'];
  return exp && typeof exp === 'object' && !Array.isArray(exp) ? (exp as Record<string, unknown>) : null;
}

/** Narrows a version-detail response's `afterState.documents` array, safely. */
function snapshotDocuments(detail: ExpedienteVersionDetailResponse | undefined): Record<string, unknown>[] {
  const docs = detail?.afterState?.['documents'];
  return Array.isArray(docs) ? (docs as Record<string, unknown>[]) : [];
}

const GENERAL_FIELDS = [
  'persona',
  'nombre',
  'docKind',
  'docId',
  'folio',
  'ref',
  'sector',
  'kind',
  'entityType',
  'dateOfBirth',
  'constitutionDate',
];
const CONTACT_FIELDS = ['email', 'phone', 'direccionFisica'];
const RISK_FIELDS = [
  'canal',
  'producto',
  'proposito',
  'paisesActividad',
  'fuenteFondos',
  'pep',
  'nextReviewAt',
  'assignedTo',
  'usPerson',
  'paisResidenciaFiscal',
  'multiplesNacionalidades',
];

function SnapshotFieldGroup({
  title,
  keys,
  exp,
}: {
  title: string;
  keys: string[];
  exp: Record<string, unknown>;
}) {
  const rows = keys.filter((k) => exp[k] !== undefined);
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink-faint">{title}</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {rows.map((k) => (
          <div key={k} className="rounded-lg border border-line px-3 py-2">
            <div className="text-[11px] font-semibold text-ink-soft">{FIELD_LABELS[k] ?? k}</div>
            <div className="mt-0.5 text-sm text-ink">{formatDiffValue(k, exp[k])}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Estado/riesgo/score are pulled out of the generic field groups and shown
 *  prominently up top — the state that mattered most at this version. */
function SnapshotHeader({ exp }: { exp: Record<string, unknown> }) {
  const estado = typeof exp['estado'] === 'string' ? (exp['estado'] as string) : null;
  const riesgo = typeof exp['riesgo'] === 'string' ? (exp['riesgo'] as string) : null;
  const score = typeof exp['score'] === 'number' ? (exp['score'] as number) : null;
  if (!estado && !riesgo && score === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {estado && <Pill tone={ESTADO_TONE[estado] ?? 'slate'}>{estado}</Pill>}
      {riesgo && (
        <Pill tone={RIESGO_TONE[riesgo] ?? 'slate'}>
          {esPA.expedienteActivity.detail.snapshotGroupRisk}: {formatDiffValue('riesgo', riesgo)}
        </Pill>
      )}
      {score !== null && <span className="font-mono text-[11px] text-ink-faint">Puntaje: {score}</span>}
    </div>
  );
}

function SnapshotDocRow({ doc }: { doc: Record<string, unknown> }) {
  const name = typeof doc['name'] === 'string' ? (doc['name'] as string) : 'Documento';
  const estado = typeof doc['estado'] === 'string' ? (doc['estado'] as string) : null;
  const deleted = doc['deletedAt'] != null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-2.5">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink">{name}</div>
        {typeof doc['category'] === 'string' && (
          <div className="text-[11px] text-ink-faint">{formatDiffValue('category', doc['category'])}</div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {estado && <Pill tone={DOC_ESTADO_TONE[estado] ?? 'slate'}>{estado}</Pill>}
        {deleted && <Badge muted>{esPA.expedienteActivity.detail.snapshotDeletedDoc}</Badge>}
      </div>
    </div>
  );
}

/** Restore affordance (confirm → restore), shared by both drawer tabs — a
 *  restorable entry can be restored from either the Cambios or the Expediente
 *  completo view. */
function RestoreSection({
  confirming,
  setConfirming,
  busy,
  restoreTargetIsDeleted,
  onConfirm,
}: {
  confirming: boolean;
  setConfirming: (v: boolean) => void;
  busy: boolean;
  restoreTargetIsDeleted: boolean;
  onConfirm: () => void;
}) {
  return (
    <div className="border-t border-line pt-4">
      {!confirming ? (
        <Button variant="outline" onClick={() => setConfirming(true)} disabled={busy}>
          <RotateCcw className="h-4 w-4" strokeWidth={2} />
          {esPA.expedienteActivity.restoreVersion}
        </Button>
      ) : (
        <div className="grid gap-3">
          <p className="text-sm text-ink-soft">{esPA.expedienteActivity.restoreVersionConfirm}</p>
          {restoreTargetIsDeleted && (
            <p className="text-sm font-semibold text-tone-amber">
              {esPA.expedienteActivity.restoreVersionDeletedWarning}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={onConfirm} disabled={busy}>
              {busy ? 'Restaurando…' : esPA.expedienteActivity.restoreVersion}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The "Cambios" before→after diff body — shared by both the Cambios tab
 *  (restorable entries) and the single-view drawer (non-restorable entries). */
function ChangesPanel({ diffs }: { diffs: ReturnType<typeof diffActivityFields> }) {
  return (
    <div>
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink-faint">
        {esPA.expedienteActivity.detail.changes}
      </div>
      {diffs.length === 0 ? (
        <p className="text-sm text-ink-faint">{esPA.expedienteActivity.detail.noChanges}</p>
      ) : (
        <div className="grid gap-2.5">
          {diffs.map((d) => (
            <div key={d.key} className="rounded-lg border border-line px-3 py-2.5">
              <div className="text-xs font-semibold text-ink-soft">{d.label}</div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px]">
                <span className="rounded bg-tone-red-bg px-1.5 py-0.5 text-tone-red line-through">
                  {esPA.expedienteActivity.detail.before}: {d.before}
                </span>
                <span className="text-ink-faint">→</span>
                <span className="rounded bg-primary/10 px-1.5 py-0.5 font-semibold text-primary-dark">
                  {esPA.expedienteActivity.detail.after}: {d.after}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type DrawerTab = 'changes' | 'snapshot';

/** The shared wide drawer: metadata + (Cambios tab) before→after diff, or
 *  (restorable entries only) the "Expediente completo" full read-only
 *  snapshot tab — either tab surfaces the inline Restaurar confirm, whose
 *  copy explicitly warns the expediente's estado/approval is also restored. */
function ActivityDetailDrawer({
  entry,
  expedienteId,
  onClose,
}: {
  entry: ExpedienteActivityEntry | null;
  expedienteId: string;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<DrawerTab>('changes');
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    setTab('changes');
    setConfirming(false);
  }, [entry?.versionKey]);

  const restoreVersion = useRestoreExpedienteVersion(expedienteId);
  const busy = restoreVersion.isPending;

  // Fetched lazily: only once "Expediente completo" is actually open, OR the
  // restore confirm is showing (so the deleted-state warning below can be
  // accurate without requiring the user to have visited that tab first).
  const versionDetail = useExpedienteVersionDetail(
    expedienteId,
    entry?.versionSeq ?? null,
    !!entry?.restorable && (tab === 'snapshot' || confirming)
  );

  function confirmRestore() {
    if (!entry || !entry.restorable || entry.versionSeq === null) return;
    restoreVersion.mutate(entry.versionSeq, { onSuccess: onClose });
  }

  const Icon = entry ? categoryIcon(entry.entityType) : History;
  const subtitle = entry ? activityCategoryLabel(entry.entityType) : undefined;
  const diffs = entry ? diffActivityFields(entry.beforeState, entry.afterState) : [];

  const snapshotExp = snapshotExpediente(versionDetail.data);
  const snapshotDocs = snapshotDocuments(versionDetail.data);

  // Restoring a version whose snapshot shows the expediente already trashed
  // (`deletedAt` non-null) re-applies THAT deleted state — an active case
  // would be moved to the papelera. Warn, but never block. Prefer the fetched
  // full snapshot (accurate for every restorable entry, document-scoped ones
  // included); fall back to the entry's own afterState only when it's itself
  // the expediente row and the snapshot hasn't loaded yet.
  const restoreTargetIsDeleted =
    !!entry?.restorable &&
    (snapshotExp
      ? snapshotExp['deletedAt'] != null
      : entry.entityType === 'expediente'
        ? entry.afterState?.['deletedAt'] != null
        : false);

  return (
    <SlideOver
      open={entry !== null}
      title={entry ? auditActionLabel(entry.action) : ''}
      subtitle={subtitle}
      onClose={onClose}
      widthClassName="max-w-2xl"
    >
      {entry && (
        <div className="grid gap-5">
          <div className="flex items-start gap-3 rounded-xl border border-line bg-canvas px-4 py-3.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-panel text-ink-soft">
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
              {entry.targetLabel && (
                <div className="truncate text-sm font-semibold text-ink">{entry.targetLabel}</div>
              )}
              <div className="mt-0.5 text-xs text-ink-soft">
                {esPA.expedienteActivity.detail.performedBy}:{' '}
                <span className="font-semibold text-ink">{entry.actorName}</span>{' '}
                <span className="font-mono text-[10.5px] text-ink-faint">· {entry.actorRole}</span>
              </div>
              <div className="mt-0.5 font-mono text-[10.5px] text-ink-faint">
                {formatDate(entry.createdAt, '—')}
              </div>
            </div>
          </div>

          {entry.restorable ? (
            <>
              <TabStrip
                tabs={[
                  { key: 'changes', label: esPA.expedienteActivity.detail.tabChanges },
                  { key: 'snapshot', label: esPA.expedienteActivity.detail.tabSnapshot },
                ]}
                active={tab}
                onChange={(k) => setTab(k as DrawerTab)}
              />

              {tab === 'changes' ? (
                <div className="grid gap-5">
                  <ChangesPanel diffs={diffs} />
                  <RestoreSection
                    confirming={confirming}
                    setConfirming={setConfirming}
                    busy={busy}
                    restoreTargetIsDeleted={restoreTargetIsDeleted}
                    onConfirm={confirmRestore}
                  />
                </div>
              ) : (
                <div className="grid gap-5">
                  <div className="text-sm font-semibold text-ink">
                    {esPA.expedienteActivity.detail.previewOfVersion.replace(
                      '{{n}}',
                      String(entry.versionSeq ?? '—')
                    )}
                  </div>
                  {versionDetail.isLoading ? (
                    <LoadingRows />
                  ) : versionDetail.isError ? (
                    <ErrorRow
                      message="No se pudo cargar la versión."
                      onRetry={() => void versionDetail.refetch()}
                    />
                  ) : !snapshotExp ? (
                    <p className="text-sm text-ink-faint">{esPA.expedienteActivity.detail.noChanges}</p>
                  ) : (
                    <>
                      <SnapshotHeader exp={snapshotExp} />
                      <SnapshotFieldGroup
                        title={esPA.expedienteActivity.detail.snapshotGroupGeneral}
                        keys={GENERAL_FIELDS}
                        exp={snapshotExp}
                      />
                      <SnapshotFieldGroup
                        title={esPA.expedienteActivity.detail.snapshotGroupContact}
                        keys={CONTACT_FIELDS}
                        exp={snapshotExp}
                      />
                      <SnapshotFieldGroup
                        title={esPA.expedienteActivity.detail.snapshotGroupRisk}
                        keys={RISK_FIELDS}
                        exp={snapshotExp}
                      />
                      <div>
                        <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink-faint">
                          {esPA.expedienteActivity.detail.snapshotDocumentsTitle}
                        </div>
                        {snapshotDocs.length === 0 ? (
                          <p className="text-sm text-ink-faint">{esPA.expedienteActivity.empty}</p>
                        ) : (
                          <div className="grid gap-2">
                            {snapshotDocs.map((d, i) => (
                              <SnapshotDocRow key={typeof d['id'] === 'string' ? d['id'] : i} doc={d} />
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <RestoreSection
                    confirming={confirming}
                    setConfirming={setConfirming}
                    busy={busy}
                    restoreTargetIsDeleted={restoreTargetIsDeleted}
                    onConfirm={confirmRestore}
                  />
                </div>
              )}
            </>
          ) : (
            <ChangesPanel diffs={diffs} />
          )}
        </div>
      )}
    </SlideOver>
  );
}

/** Default export: the one collapsed-by-default section + the unified
 *  timeline + the one shared drawer. Mount once per `ExpedienteDetail`. */
export default function ExpedienteActivitySection({ expedienteId }: { expedienteId: string }) {
  const [open, setOpen] = useState(false);
  const activity = useExpedienteActivity(expedienteId);
  const [selected, setSelected] = useState<ExpedienteActivityEntry | null>(null);

  const entries = activity.data?.pages.flatMap((p) => p.entries) ?? [];
  const hasMore = !!activity.hasNextPage;

  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left"
      >
        <History className="h-4 w-4 shrink-0 text-ink-soft" strokeWidth={1.75} />
        <span className="flex-1 text-sm font-bold text-ink">{esPA.expedienteActivity.sectionTitle}</span>
        <Pill tone="slate" className="shrink-0">
          {entries.length}
          {hasMore ? '+' : ''}
        </Pill>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-ink-faint transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div className="border-t border-line px-5 py-5">
          <ActivityTimeline
            entries={entries}
            isLoading={activity.isLoading}
            isError={activity.isError}
            onRetry={() => void activity.refetch()}
            onSelect={(entry) => setSelected(entry)}
            hasNextPage={hasMore}
            isFetchingNextPage={activity.isFetchingNextPage}
            onLoadMore={() => void activity.fetchNextPage()}
          />
        </div>
      )}

      <ActivityDetailDrawer entry={selected} expedienteId={expedienteId} onClose={() => setSelected(null)} />
    </Card>
  );
}
